import type { IMatch } from '@/interface/IMatch';
import type { IMarketGroup, IOdds, IPriceOption } from '@/interface/IOdds';
import type { IOutright, IOutrightTournament } from '@/interface/IOutright';
import type { ITicketOddsInfo } from '@/interface/ITicketResponse';
import type { ITranslation } from '@/interface/ITranslation';
import type { BetPageType, SportType } from '@/interface/enum';
import {
    MarketPage,
    MarketType,
    OddsOption,
    OpenTicketStatus,
    PriceStyle,
} from '@/interface/enum';

export interface IEuroOutrightTicket {
    type: 'outright';
    id: number;
    marketDisplayTypeName: string;
    marketType: MarketType;
    marketPageType: MarketPage;
    betPage: BetPageType;
    minBet: number;
    maxBet: number;
    priceStyle: number;
    message: {
        timeoutId: number;
        key: string | null;
        data: Record<string, string> | null;
    };
    uid: string;
    option: string;
    sportType: SportType;
    leagueId: number;
    leagueName: ITranslation[];
    isLive: boolean;
    price: number;
    eventDate: string;
    eventCode: string;
    isWalkinOnly: boolean;
    teamName: ITranslation[];
}

export interface IEuroTicket {
    type: 'single';
    key: string;
    event: IMatch;
    odds: IOdds;
    priceOption: IPriceOption;
    betPage: BetPageType;
    minBet: number;
    maxBet: number;
    priceStyle: number;
    message: {
        timeoutId: number;
        key: string | null;
        data: Record<string, string> | null;
    };
    uid: string;
    sportType: number;
    leagueId: number;
    leagueName: string | ITranslation[];
    eventId: number;
    oddsId: number;
    point: number;
    marketDisplayTypeName: string;
    marketType: number;
    option: string;
    optionName: string;
    betCondition: string;
    isLive: boolean;
    price: number;
    isMixParlay: boolean;
    marketPageType: MarketPage;
    marketGroup: IMarketGroup;
}

export function createEuroTicket(
    event: IMatch,
    odds: IOdds,
    priceOption: IPriceOption,
    betPage: BetPageType,
    isMixParlay: boolean,
): IEuroTicket {
    return {
        type: 'single',
        key: `${odds.oddsId}-${priceOption.option}`,
        event,
        odds,
        priceOption,
        betPage,
        minBet: 0,
        maxBet: 0,
        priceStyle: 0,
        message: {
            key: null,
            data: null,
            timeoutId: 0,
        },
        uid: '',

        // redundant derived properties
        sportType: event.sportType,
        leagueId: event.leagueId,
        leagueName: event.leagueName ?? '',
        eventId: event.eventId,
        oddsId: odds.oddsId,
        point: odds.point,
        marketDisplayTypeName: odds.marketDisplayTypeName,
        marketType: odds.marketType,
        option: priceOption.option,
        optionName: priceOption.optionName, // NOSONAR
        betCondition: odds.betCondition,
        isLive: odds.isLive,
        price: priceOption.price,
        isMixParlay,
        marketPageType: event.marketPage,
        marketGroup: odds.marketGroup,
    };
}

// A bet slip snapshot written by a pre-oddsOption bundle lacks the enum field.
// Resolve it during rehydration so display logic remains correct across deploys.
export function withResolvedOddsOption(ticket: IEuroTicket): IEuroTicket {
    return {
        ...ticket,
        priceOption: resolveStoredPriceOption(ticket.odds.marketType, ticket.priceOption),
        odds: {
            ...ticket.odds,
            priceOption: ticket.odds.priceOption.map(
                priceOption => resolveStoredPriceOption(ticket.odds.marketType, priceOption),
            ),
        },
    };
}

export function withResolvedOddsOptions(tickets: Record<string, IEuroTicket>): Record<string, IEuroTicket> {
    return Object.fromEntries(Object.entries(tickets)
        .map(([key, ticket]) => [key, withResolvedOddsOption(ticket)]));
}

function resolveStoredPriceOption(marketType: MarketType, priceOption: IPriceOption): IPriceOption {
    if (priceOption.oddsOption !== undefined) {
        return priceOption;
    }
    return {
        ...priceOption,
        oddsOption: OddsOption.fromOddsApi(MarketType.toOddsApi(marketType), priceOption.option),
    };
}

export function createEuroOutrightTicket(outright: IOutright, oddFrom: BetPageType, outrightTournament: IOutrightTournament): IEuroOutrightTicket {
    return {
        type: 'outright',
        id: outright.id,
        marketDisplayTypeName: 'marketType_OutRight',
        marketType: MarketType.OutRight,
        betPage: oddFrom,
        marketPageType: MarketPage.Outright,
        minBet: 0,
        maxBet: 0,
        priceStyle: PriceStyle.Euro,
        message: {
            key: null,
            data: null,
            timeoutId: 0,
        },
        uid: '',
        sportType: outrightTournament.sportType,
        leagueId: outrightTournament.id,
        leagueName: outrightTournament.leagueName,
        option: 'or',
        isLive: outright.isLive,
        price: outright.price,
        eventDate: outright.eventDate,
        eventCode: outright.eventCode,
        isWalkinOnly: outright.isWalkinOnly,
        teamName: outright.teamName,
    };
}

export function getOpenTicketErrorMessage(code: OpenTicketStatus | undefined, oldData: IEuroTicket | IEuroOutrightTicket, newData: ITicketOddsInfo, shouldCompareOdds: boolean) {
    switch (code) {
        case OpenTicketStatus.InvalidInput:
        case OpenTicketStatus.InvalidPrice:
            return 'betSlip_errorMessage_generalFailure';
        case OpenTicketStatus.EventClosed:
        case OpenTicketStatus.EventCompleted:
        case OpenTicketStatus.EventInternal:
            return 'betSlip_errorMessage_eventClosed';
        case OpenTicketStatus.OddsClosed:
        case OpenTicketStatus.OddsCompleted:
        case OpenTicketStatus.OddsInternal:
            return 'betSlip_errorMessage_oddsClosed';
        case OpenTicketStatus.MaxOddsExceed:
            return 'betSlip_errorMessage_maxOddsExceed';
        default:
            return shouldCompareOdds
                ? compareOddsMessage(oldData, newData)
                : null;
    }
}

function compareOddsMessage(oldData: IEuroTicket | IEuroOutrightTicket, newData: ITicketOddsInfo) {
    if (oldData.price !== newData.price) {
        return 'betSlip_errorMessage_oddsChanged';
    }
    if (oldData.type === 'single' && oldData.odds
        && (oldData.odds.eventResult.liveHomeScore !== newData.liveHomeScore
        || oldData.odds.eventResult.liveAwayScore !== newData.liveAwayScore)
    ) {
        return 'betSlip_errorMessage_scoreChanged';
    }
    if (oldData.type === 'single' && oldData.point !== undefined && newData.point !== undefined && oldData.point !== newData.point) {
        return 'betSlip_errorMessage_pointChanged';
    }

    return null;
}
